import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Camera, CheckCircle, X, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Live camera capture with no upload-from-gallery option. Used for KYC-level
 * artifacts (driver selfie, license, vehicle photos) where we need to ensure
 * the image was actually taken at signup/refresh time on a real device.
 *
 * Props:
 *   - label, helper, icon: UI text + idle-state icon
 *   - facing: "user" (selfie) | "environment" (rear camera)
 *   - aspect: "4/3" | "3/2"
 *   - value: { blob, dataUrl } | null
 *   - onChange: (value | null) => void
 *   - testidPrefix: data-testid scoping
 */
export default function LiveCameraCapture({
  label,
  helper,
  facing = "user",
  aspect = "4/3",
  value,
  onChange,
  testidPrefix,
  icon,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState(null);

  const stop = React.useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStreaming(false);
  }, []);

  const start = async () => {
    if (starting || streaming) return;
    setErr(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Your browser doesn't support camera access. Please use Chrome or Safari on a phone.");
      return;
    }
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      setErr("Camera access requires HTTPS. Please use the secure URL.");
      return;
    }
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      setStreaming(true);
    } catch (e) {
      console.error("camera err", e);
      let msg = "Camera unavailable. Please grant camera permissions and try again.";
      if (e.name === "NotAllowedError") msg = "Camera access denied. Please allow camera permissions in your browser settings and reload.";
      else if (e.name === "NotFoundError") msg = "No camera detected on this device.";
      else if (e.name === "NotReadableError") msg = "Camera is already in use by another app.";
      setErr(msg);
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (streaming && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch((e) => {
        console.warn("video.play() rejected", e);
      });
    }
  }, [streaming]);

  useEffect(() => () => stop(), [stop]);

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) {
      toast.error("Camera isn't ready yet. Try again in a second.");
      return;
    }
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob(
      (blob) => {
        if (!blob) { toast.error("Couldn't capture image"); return; }
        const dataUrl = c.toDataURL("image/jpeg", 0.85);
        onChange({ blob, dataUrl });
        stop();
      },
      "image/jpeg",
      0.85
    );
  };

  const retake = () => {
    onChange(null);
    start();
  };

  const aspectClass = aspect === "3/2" ? "aspect-[3/2]" : "aspect-[4/3]";

  return (
    <div data-testid={`${testidPrefix}-section`}>
      {label && <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>}
      {helper && <p className="text-xs text-zinc-500 mt-0.5 mb-2">{helper}</p>}
      <div className={`bg-zinc-900 rounded-md ${aspectClass} overflow-hidden flex items-center justify-center relative`}>
        {value ? (
          <>
            <img src={value.dataUrl} alt="capture preview" className="w-full h-full object-cover" data-testid={`${testidPrefix}-preview`} />
            <button
              type="button"
              onClick={retake}
              className="absolute top-2 right-2 px-2.5 py-1.5 rounded bg-white/95 text-zinc-900 text-xs font-medium shadow-md flex items-center gap-1 hover:bg-white"
              data-testid={`${testidPrefix}-retake`}
            >
              <ArrowsClockwise size={11} weight="bold" /> Retake
            </button>
            <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-emerald-600 text-white text-[10px] font-semibold flex items-center gap-1">
              <CheckCircle size={10} weight="bold" /> Live capture
            </div>
          </>
        ) : (
          <>
            <video
              ref={videoRef}
              className={`w-full h-full object-cover transition-opacity ${streaming ? "opacity-100" : "opacity-0 pointer-events-none absolute"}`}
              playsInline
              muted
              autoPlay
              data-testid={`${testidPrefix}-video`}
            />
            {streaming ? (
              <>
                <div className="absolute inset-4 border-2 border-white/60 rounded-md pointer-events-none" />
                <button
                  type="button"
                  onClick={capture}
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-white border-4 border-[#002BF6] shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
                  data-testid={`${testidPrefix}-capture-btn`}
                  aria-label="Capture photo"
                >
                  <Camera size={22} weight="bold" className="text-[#002BF6]" />
                </button>
                <button
                  type="button"
                  onClick={stop}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60"
                  data-testid={`${testidPrefix}-cancel-btn`}
                  aria-label="Cancel"
                >
                  <X size={14} weight="bold" />
                </button>
              </>
            ) : (
              <div className="text-center p-6 text-zinc-200 absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-zinc-300 mb-3">{icon}</div>
                <Button
                  type="button"
                  onClick={start}
                  disabled={starting}
                  className="bg-white text-zinc-900 hover:bg-zinc-100"
                  data-testid={`${testidPrefix}-start-btn`}
                >
                  {starting ? "Starting camera…" : "Open camera"}
                </Button>
                {err && <div className="text-xs text-red-300 mt-3 max-w-xs">{err}</div>}
              </div>
            )}
          </>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
